import { clsx } from 'clsx'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import {
  Badge,
  CircleCheckBigIcon,
  Flex,
  Progress,
  Text,
} from '@buffer-mono/popcorn'

import { formatUnrepliedCount } from './commentMetricsUtils'
import type { CommentCounts } from './commentMetricsUtils'
import { EASE_OUT } from '../../../util/easingRules'

import metricsStyles from './CommentMetrics.module.css'
import styles from './CommentMetricsVariants.module.css'

import type { JSX } from 'react'

export type MetricsData = {
  isAllAddressed: boolean
  hasComments: boolean
  animatedPercentage: number
  percentage: number
  color: 'critical' | 'warning' | 'success' | undefined
  countsForDisplay: CommentCounts
  total: number
  interactedFormatted: string
  totalFormatted: string
}

type VariantProps = {
  data: MetricsData
  isActive: boolean
  className?: string
}

/**
 * Grid variant:
 * - Default: progress bar + unreplied badge (or check icon when all addressed)
 * - Hover: crossfades to interacted/total count, right-aligned
 */
export function GridCommentMetrics({
  data,
  isActive,
  className,
}: VariantProps): JSX.Element {
  const { isAllAddressed, hasComments, total, percentage, countsForDisplay } =
    data

  if (!hasComments) {
    return (
      <div
        className={className}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The named group summarizes the post's comment metrics.
        role="group"
        aria-label="No comments on this post"
      >
        <Flex align="center" gap="xs" fullWidth>
          <Progress
            value={0}
            size="small"
            variant="classic"
            className={metricsStyles.progressBar}
            aria-label="0% of comments addressed"
          />
        </Flex>
      </div>
    )
  }

  return (
    <div
      className={className}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The named group summarizes the post's comment metrics.
      role="group"
      aria-label={
        isAllAddressed
          ? `All ${total} comments addressed`
          : `${percentage}% addressed, ${countsForDisplay.unreplied} unreplied`
      }
    >
      <div className={clsx(styles.crossfade, styles.crossfadeWide)}>
        {/* Default layer */}
        <div
          className={clsx(
            styles.crossfadeLayer,
            isActive && styles.crossfadeHidden,
          )}
        >
          {isAllAddressed ? (
            <Flex justify="end" fullWidth>
              <CircleCheckBigIcon size={12} color="subtle" />
            </Flex>
          ) : (
            <Flex align="center" gap="xs" fullWidth>
              <ProgressBar
                value={data.animatedPercentage}
                percentage={data.percentage}
                color={data.color}
              />
              <UnrepliedBadge count={countsForDisplay.unreplied} />
            </Flex>
          )}
        </div>
        {/* Active layer (hover / selected) */}
        <div
          className={clsx(
            styles.crossfadeLayer,
            styles.crossfadeEndAligned,
            !isActive && styles.crossfadeHidden,
          )}
        >
          <CountText
            interacted={data.interactedFormatted}
            total={data.totalFormatted}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * List variant:
 * - Default: progress bar + unreplied badge (or check icon when all addressed)
 * - Hover: badge crossfades to count; for all-addressed, count slides in beside check icon
 */
export function ListCommentMetrics({
  data,
  isActive,
  className,
}: VariantProps): JSX.Element | null {
  const { isAllAddressed, hasComments, total, percentage, countsForDisplay } =
    data

  if (!hasComments) return null

  return (
    <div className={className}>
      <AnimatePresence mode="wait" initial={false}>
        {isAllAddressed ? (
          <m.div
            key="addressed"
            className={styles.endAligned}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE_OUT.cubic }}
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The animated metric summary remains a named group for assistive technology.
            role="group"
            aria-label={`All ${total} comments addressed`}
          >
            <Flex align="center" gap="2xs">
              <AnimatePresence initial={false}>
                {isActive && (
                  <m.div
                    key="count"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2, ease: EASE_OUT.cubic }}
                    className={metricsStyles.inlineCount}
                  >
                    <CountText
                      interacted={data.interactedFormatted}
                      total={data.totalFormatted}
                    />
                  </m.div>
                )}
              </AnimatePresence>
              <CircleCheckBigIcon size={12} color="subtle" />
            </Flex>
          </m.div>
        ) : (
          <div
            key="progress"
            className={styles.fullWidth}
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The progress summary is exposed as one named metric group.
            role="group"
            aria-label={`${percentage}% addressed, ${countsForDisplay.unreplied} unreplied`}
          >
            <Flex align="center" gap="xs" fullWidth>
              <ProgressBar
                value={data.animatedPercentage}
                percentage={data.percentage}
                color={data.color}
              />
              {/* Badge / count crossfade */}
              <div className={styles.crossfade}>
                <div
                  className={clsx(
                    styles.crossfadeLayer,
                    isActive && styles.crossfadeHidden,
                  )}
                >
                  <UnrepliedBadge count={countsForDisplay.unreplied} />
                </div>
                <div
                  className={clsx(
                    styles.crossfadeLayer,
                    !isActive && styles.crossfadeHidden,
                  )}
                >
                  <CountText
                    interacted={data.interactedFormatted}
                    total={data.totalFormatted}
                  />
                </div>
              </div>
            </Flex>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Compact variant:
 * - Always shows bar + count + badge. No hover interactions.
 * - Used by CompactPostNavigationCard (sticky header).
 */
export function CompactCommentMetrics({
  data,
  className,
}: Omit<VariantProps, 'isActive'>): JSX.Element {
  const { hasComments, countsForDisplay, percentage, total } = data
  const interacted = countsForDisplay.replied + countsForDisplay.dismissed

  return (
    <div className={className}>
      <Flex
        align="center"
        gap="xs"
        fullWidth
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The metric values form one named group for assistive technology.
        role="group"
        aria-label={
          hasComments
            ? `${percentage}% addressed, ${interacted} of ${total} handled, ${countsForDisplay.unreplied} unreplied`
            : 'No comments on this post'
        }
      >
        <ProgressBar
          value={data.animatedPercentage}
          percentage={data.percentage}
          color={data.color}
        />
        {hasComments && (
          <CountText
            interacted={data.interactedFormatted}
            total={data.totalFormatted}
          />
        )}
        <UnrepliedBadge count={countsForDisplay.unreplied} />
      </Flex>
    </div>
  )
}

function CountText({
  interacted,
  total,
}: {
  interacted: string
  total: string
}): JSX.Element {
  return (
    <Text size="sm" className={metricsStyles.countText}>
      {interacted}
      <Text as="span" size="sm" color="subtle">
        /{total}
      </Text>
    </Text>
  )
}

function ProgressBar({
  value,
  percentage,
  color,
}: {
  value: number
  percentage: number
  color: MetricsData['color']
}): JSX.Element {
  return (
    <Progress
      value={value}
      size="small"
      variant="classic"
      className={metricsStyles.progressBar}
      data-color={color}
      aria-label={`${percentage}% addressed`}
    />
  )
}

function UnrepliedBadge({ count }: { count: number }): JSX.Element | null {
  if (count <= 0) return null
  return (
    <Badge
      size="small"
      variant="brand-subtle"
      aria-label={`${count} unreplied comments`}
    >
      {formatUnrepliedCount(count)}
    </Badge>
  )
}
